import {openDeleteDialog, Button, showNotification, useDialog} from 'platform/components';
import {Box, Heading, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useActivateTeamMemberMutation,
  useAddTeamMembersMutation,
  useDeactivateTeamMemberMutation,
  useDeleteTeamMemberMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  useGetUserOptions,
  usePermissions,
} from '@dms/shared';

import {getSafely, useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {AddMemberDialog} from './AddMemberDialog';

const GRID_CODE = 'team-member';
const SUCCESS_MESSAGES = {
  delete: 'general.notifications.successfullyDeleted',
  deactivate: 'general.notifications.blockedSuccess',
  activate: 'general.notifications.unblockedSuccess',
};

export function MembersOverview() {
  const {id} = useRequiredParams();
  const {userOptions} = useGetUserOptions();
  const [addMembers, {isLoading}] = useAddTeamMembersMutation();
  const [deleteMember] = useDeleteTeamMemberMutation();
  const [deactivateMember] = useDeactivateTeamMemberMutation();
  const [activateMember] = useActivateTeamMemberMutation();
  const [isOpen, onOpen, onClose, {key}] = useDialog();

  const [canUpdateTeam] = usePermissions({
    permissionKeys: ['teamUpdate'],
  });

  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {team: id}]);

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const memberId = isArray(rowId) ? rowId[0] : rowId;

    const payload = {
      teamId: id,
      memberId,
    };

    const handleSuccess = (actionKey: 'delete' | 'deactivate' | 'activate') => {
      const successMessageKey = getSafely(
        SUCCESS_MESSAGES,
        actionKey,
        'general.notifications.success'
      ) as string;

      showNotification.success(i18n.t(successMessageKey));
      refreshData();
    };

    match(actionKey)
      .with('delete', (actionKey) =>
        openDeleteDialog({
          onConfirm: () =>
            deleteMember(payload)
              .unwrap()
              .then(() => handleSuccess(actionKey))
              .catch(handleApiError),
        })
      )
      .with('deactivate', (actionKey) =>
        deactivateMember(payload)
          .unwrap()
          .then(() => handleSuccess(actionKey))
          .catch(handleApiError)
      )
      .with('activate', (actionKey) =>
        activateMember(payload)
          .unwrap()
          .then(() => handleSuccess(actionKey))
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleSaveMembers = (userIds: string[]) => {
    if (userIds.length === 0) {
      return;
    }

    addMembers({
      teamId: id,
      addTeamMembersRequestBody: {userIds},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.success'));
        refreshDmsDatagrid(GRID_CODE);
        onClose();
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4} data-testid={testIds.settings.teamManagementDetail('membersOverview')}>
      <HStack justify="space-between" align="center">
        <Heading size={4} data-testid={testIds.settings.teamManagementDetail('membersHeading')}>
          {i18n.t('general.labels.teamMembers')}
        </Heading>
        <Button
          variant="secondary"
          title={i18n.t('page.settings.actions.addTeamMember')}
          onClick={onOpen}
          data-testid={testIds.settings.teamManagementDetail('addMemberButton')}
          isDisabled={!canUpdateTeam}
        />
      </HStack>

      <Box>
        <DmsDataGrid
          gridCode={GRID_CODE}
          queryModifier={queryModifier}
          actionCallback={actionCallback}
          autoHeight
          data-testid={testIds.settings.teamManagementDetail('membersDG')}
        />
      </Box>

      <AddMemberDialog
        key={key}
        isOpen={isOpen}
        onClose={onClose}
        onSave={handleSaveMembers}
        userOptions={userOptions}
        isLoading={isLoading}
      />
    </VStack>
  );
}
